import React, { useEffect, useRef, useState } from "react";
import { Upload, Trash2, FileText, Download } from "lucide-react";
import api, { API_BASE, formatErr } from "../lib/api";
import RateCardEditor from "./RateCardEditor";

const DISCIPLINES = [
  { value: "", label: "(unspecified)" },
  { value: "process", label: "Process" },
  { value: "mechanical", label: "Mechanical" },
  { value: "piping", label: "Piping" },
  { value: "electrical", label: "Electrical" },
  { value: "instrumentation", label: "Instrumentation" },
  { value: "civil", label: "Civil / Structural" },
  { value: "fire", label: "Fire Protection" },
  { value: "architectural", label: "Architectural" },
  { value: "traffic", label: "Traffic / Highway" },
  { value: "drainage", label: "Drainage / Hydraulics" },
];

function fmtBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function CustomStandardsPanel({ projectId, project, onChange, compact = false }) {
  const [items, setItems] = useState([]);
  const [discipline, setDiscipline] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/standards`);
      setItems(data);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { if (projectId) load(); }, [projectId]); // eslint-disable-line

  const upload = async (file) => {
    if (!file) return;
    setErr(""); setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    if (discipline) fd.append("discipline", discipline);
    if (description) fd.append("description", description);
    try {
      await api.post(`/projects/${projectId}/standards`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDescription("");
      await load();
      onChange?.();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this standard from the project?")) return;
    try {
      await api.delete(`/projects/${projectId}/standards/${id}`);
      await load();
      onChange?.();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const used = project?.standards_used_bytes ?? items.reduce((a, x) => a + (x.size_bytes || 0), 0);
  const quota = project?.standards_quota_bytes ?? 524_288_000;
  const pct = Math.min(100, (used / quota) * 100);

  return (
    <div data-testid="custom-standards-panel">
      {!compact && (
        <div className="pf-overline" style={{ marginBottom: 8 }}>CUSTOM STANDARDS · COMPANY / CLIENT / PROJECT-SPECIFIC</div>
      )}

      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ height: 6, background: "var(--pf-surface-2)", border: "1px solid var(--pf-border)", borderRadius: 2, overflow: "hidden" }}>
            <div style={{ width: `${pct}%`, height: "100%", background: pct > 85 ? "var(--pf-err)" : "var(--pf-accent)", transition: "width .25s" }} />
          </div>
          <div style={{ marginTop: 4, fontSize: 11, color: "var(--pf-text-muted)", fontFamily: "'JetBrains Mono', monospace" }} data-testid="standards-quota">
            {fmtBytes(used)} of {fmtBytes(quota)} used · 100 MB / file
          </div>
        </div>
        {pct > 85 && (
          <a href="mailto:support@rachana.ai?subject=Request%20more%20standards%20quota" className="pf-link" data-testid="standards-request-more">
            Request more
          </a>
        )}
      </div>

      <div className="pf-card" style={{ padding: 14, marginBottom: 14, border: "1px dashed var(--pf-border)" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
          <div>
            <label className="pf-label">DISCIPLINE</label>
            <select className="pf-input" value={discipline} onChange={(e) => setDiscipline(e.target.value)} data-testid="standards-discipline">
              {DISCIPLINES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </div>
          <div>
            <label className="pf-label">DESCRIPTION (OPTIONAL)</label>
            <input className="pf-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Client specification XYZ-001" data-testid="standards-description" />
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <input
            ref={inputRef}
            type="file"
            onChange={(e) => upload(e.target.files?.[0])}
            disabled={busy}
            data-testid="standards-file-input"
            style={{ flex: 1, fontSize: 12, color: "var(--pf-text-muted)" }}
          />
          {busy && <span className="pf-spin" />}
        </div>
        <div style={{ marginTop: 8, fontSize: 11, color: "var(--pf-text-dim)", fontFamily: "'JetBrains Mono', monospace" }}>
          PDF · DOCX · XLSX · TXT · scanned images · up to 100 MB / file
        </div>
      </div>

      {err && (
        <div className="pf-sev pf-sev-error" style={{ display: "block", padding: "8px 10px", marginBottom: 10 }} data-testid="standards-error">{err}</div>
      )}

      {items.length === 0 ? (
        <div style={{ color: "var(--pf-text-muted)", fontSize: 13, fontStyle: "italic" }} data-testid="standards-empty">
          No custom standards uploaded yet. Upload your company / client specs and the AI will reference them with priority over the auto-loaded industry pack.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {items.map((s) => (
            <div key={s.id} className="pf-card" style={{ padding: 12, display: "flex", alignItems: "center", gap: 12 }} data-testid={`standard-row-${s.id}`}>
              <FileText size={18} color="var(--pf-spark)" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="pf-heading" style={{ fontSize: 14, margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {s.filename}
                </div>
                <div style={{ fontSize: 11, color: "var(--pf-text-muted)", fontFamily: "'JetBrains Mono', monospace", marginTop: 2 }}>
                  {fmtBytes(s.size_bytes)} · {(s.discipline || "any discipline").toUpperCase()}{s.description ? ` · ${s.description}` : ""}
                </div>
              </div>
              <a
                className="btn btn-ghost"
                href={`${API_BASE}/projects/${projectId}/standards/${s.id}/download`}
                data-testid={`standard-download-${s.id}`}
              >
                <Download size={13} />
              </a>
              <button className="btn btn-danger" onClick={() => remove(s.id)} data-testid={`standard-delete-${s.id}`}>
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      )}

      {!compact && (
        <div style={{ marginTop: 24 }}>
          <RateCardEditor projectId={projectId} />
        </div>
      )}
    </div>
  );
}
